import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { userService } from '../../services/firestoreService'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Home,
  BarChart3,
  LayoutDashboard,
  Database,
  User,
  Menu,
  X,
  LogOut,
  Building2,
  Sun,
  Moon,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react'
import type { User as UserType } from '@shared/schemas/user'

interface SidebarProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Home', href: '/home', icon: Home },
  { name: 'Charts', href: '/charts', icon: BarChart3 },
  { name: 'Dashboards', href: '/dashboards', icon: LayoutDashboard },
  { name: 'Data Sources', href: '/data-sources', icon: Database },
  { name: 'Organization', href: '/organization', icon: Building2 },
  { name: 'Profile', href: '/profile', icon: User },
]

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [userProfile, setUserProfile] = useState<UserType | null>(null)
  const location = useLocation()
  const { currentUser, logout } = useAuth()
  const { isDark, setTheme } = useTheme()

  // Load user profile when currentUser changes
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser?.uid) {
        try {
          const profile = await userService.getById(currentUser.uid)
          setUserProfile(profile)
        } catch (error) {
          console.error('Error loading user profile for sidebar:', error)
        }
      } else {
        setUserProfile(null)
      }
    }

    loadUserProfile()
  }, [currentUser])

  // Listen for profile updates from other components
  useEffect(() => {
    const handleProfileUpdate = async () => {
      if (currentUser?.uid) {
        try {
          const profile = await userService.getById(currentUser.uid)
          setUserProfile(profile)
        } catch (error) {
          console.error('Error refreshing user profile for sidebar:', error)
        }
      }
    }

    window.addEventListener('profileUpdated', handleProfileUpdate)
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate)
  }, [currentUser])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark')
  }

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-gunmetal-800 px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center justify-between">
              <img
                className="h-8 w-auto"
                src="/logo/fireforge-logo-dark.svg"
                alt="FireForge"
              />
              <button
                type="button"
                className="text-gray-300 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-8">
              <ul className="space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 ${
                          isActive
                            ? 'bg-primary text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gunmetal-700'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </nav>
            <div className="absolute bottom-4 left-6 right-6">
              {/* Theme Toggle */}
              <div className="border-t border-gunmetal-600 pt-4 pb-4">
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-x-3 p-2 text-sm text-gray-300 hover:text-white hover:bg-gunmetal-700 rounded-md transition-colors"
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>
              <div className="flex items-center gap-x-3 p-2 text-sm text-gray-300 border-t border-gunmetal-600 pt-4">
                <Link
                  to="/profile"
                  className="flex items-center gap-x-3 flex-1 min-w-0 text-left hover:bg-gunmetal-700 rounded-md p-1 transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userProfile?.photoURL || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(userProfile?.displayName || userProfile?.email || currentUser?.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {userProfile?.displayName || currentUser?.displayName || currentUser?.email}
                    </p>
                  </div>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-white p-1 rounded-md hover:bg-gunmetal-700"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
        collapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className={`flex grow flex-col gap-y-5 overflow-y-auto bg-gunmetal-800 pb-4 ${
          collapsed ? 'px-2' : 'px-6'
        }`}>
          <div className={`flex h-16 shrink-0 items-center ${
            collapsed ? 'justify-center flex-col gap-y-1' : 'justify-between'
          }`}>
            {!collapsed ? (
              <>
                <img
                  className="h-8 w-auto"
                  src="/logo/fireforge-logo-dark.svg"
                  alt="FireForge"
                />
                <button
                  type="button"
                  className="text-gray-300 hover:text-white p-1 rounded-md hover:bg-gunmetal-700"
                  onClick={() => setCollapsed(true)}
                >
                  <Menu className="h-5 w-5" />
                </button>
              </>
            ) : (
              <button
                type="button"
                className="text-gray-300 hover:text-white p-0.5 rounded-md hover:bg-gunmetal-700"
                onClick={() => setCollapsed(false)}
              >
                <Menu className="h-4 w-4" />
              </button>
            )}
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`group flex rounded-md text-sm font-semibold leading-6 ${
                            collapsed 
                              ? 'justify-center p-1.5' 
                              : 'gap-x-3 p-2'
                          } ${
                            isActive
                              ? 'bg-primary text-white'
                              : 'text-gray-300 hover:text-white hover:bg-gunmetal-700'
                          }`}
                          title={collapsed ? item.name : undefined}
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {!collapsed && item.name}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </li>
              <li className="mt-auto">
                {/* Collapse/Expand Button (Icon only, right-aligned or centered) */}
                <div className={`pt-2 pb-2 ${collapsed ? 'flex justify-center px-0' : 'flex justify-end px-2'}`}>
                  <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="text-gray-300 hover:text-white hover:bg-gunmetal-700 rounded-md p-1.5 transition-colors"
                    title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                  >
                    {collapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-5 w-5" />}
                  </button>
                </div>

                {/* Theme Toggle */}
                <div className={`border-t border-gunmetal-600 pt-4 pb-4 ${collapsed ? 'px-0' : 'px-2'}`}>
                  <button
                    onClick={toggleTheme}
                    className={`flex items-center text-sm text-gray-300 hover:text-white hover:bg-gunmetal-700 rounded-md transition-colors ${
                      collapsed 
                        ? 'justify-center p-1.5 w-full' 
                        : 'gap-x-3 p-2 w-full'
                    }`}
                    title={collapsed ? (isDark ? 'Light Mode' : 'Dark Mode') : undefined}
                  >
                    {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                    {!collapsed && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
                  </button>
                </div>
                
                {/* User Profile Link & Logout Button DIV */}
                <div className={`flex items-center text-sm text-gray-300 border-t border-gunmetal-600 pt-4 ${
                  collapsed ? 'flex-col gap-y-2 px-0' : 'gap-x-3 p-2'
                }`}>
                  {collapsed ? (
                    <>
                      <Link
                        to="/profile"
                        className="flex items-center justify-center hover:bg-gunmetal-700 rounded-md p-1 transition-colors"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={userProfile?.photoURL || undefined} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {getInitials(userProfile?.displayName || userProfile?.email || currentUser?.email)}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-gray-300 hover:text-white p-1 rounded-md hover:bg-gunmetal-700"
                        title="Logout"
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/profile"
                        className="flex items-center gap-x-3 flex-1 min-w-0 text-left hover:bg-gunmetal-700 rounded-md p-1 transition-colors"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={userProfile?.photoURL || undefined} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {getInitials(userProfile?.displayName || userProfile?.email || currentUser?.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {userProfile?.displayName || currentUser?.displayName || currentUser?.email}
                          </p>
                        </div>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-gray-300 hover:text-white p-1 rounded-md hover:bg-gunmetal-700"
                        title="Logout"
                      >
                        <LogOut className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-background px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-foreground lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <div className="flex items-center gap-x-4 lg:gap-x-6">
            <img
              className="h-8 w-auto"
              src="/logo/fireforge-logo.svg"
              alt="FireForge"
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Sidebar 